import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import bkstApi from "../../api";
import { anyWordStartsWith, capitalize, equalsIgnoreCase } from "../../common/util";
import ItemFilter from "../../planner/component/ItemFilter";
import SearchIcon from "@mui/icons-material/Search";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import TimelineIcon from "@mui/icons-material/Timeline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import StockUpdateDialog from "../component/StockUpdateDialog";
import { useReactToPrint } from "react-to-print";
import { bakeStreetLogo } from "../../common/constants";
import moment from "moment";

const LOW_STOCK_THRESHOLD = 10;

export default function Inventory(props) {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [productFilter, setProductFilter] = useState({});

  // Dialog state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAction, setSelectedAction] = useState("add");
  const [dialogOpen, setDialogOpen] = useState(false);

  // Refs for print and CSV export
  const printRef = useRef();
  const dataGridApiRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    bkstApi(`/my/product?filter=active`).then((res) => {
      const sortedProducts = res.data.products.sort((a, b) => {
        // Sort by stock (low stock first), then by title
        const stockA = a.stock ?? 0;
        const stockB = b.stock ?? 0;
        if (stockA !== stockB) return stockA - stockB;
        return a.title.localeCompare(b.title);
      });
      setProducts(sortedProducts);
      setFiltered(sortedProducts);
      setLoading(false);
    });
  }, []);

  // Apply filters to products
  useEffect(() => {
    let filteredProducts = products;

    // Apply text query filter
    if (query) {
      filteredProducts = filteredProducts.filter((p) => anyWordStartsWith(p.title, query));
    }

    // Apply product filters (category)
    if (Object.keys(productFilter).length > 0) {
      filteredProducts = filteredProducts.filter((product) => {
        for (const [key, filterValue] of Object.entries(productFilter)) {
          const hasValue = Array.isArray(filterValue?.value)
            ? filterValue.value.length > 0
            : filterValue?.value;
          if (hasValue) {
            if (key === "category") {
              const categories = product.category || [product.type?.toLowerCase()];
              const filterValues = Array.isArray(filterValue.value)
                ? filterValue.value
                : [filterValue.value];
              if (
                !filterValues.some((filterCat) =>
                  categories.some((cat) => cat.toLowerCase() === filterCat.toLowerCase())
                )
              ) {
                return false;
              }
            }
          }
        }
        return true;
      });
    }

    setFiltered(filteredProducts);
  }, [products, query, productFilter]);

  // Handle opening dialog
  const handleOpenDialog = (product, action) => {
    setSelectedProduct(product);
    setSelectedAction(action);
    setDialogOpen(true);
  };

  // Handle closing dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProduct(null);
    setSelectedAction("add");
  };

  // Handle stock update
  const handleStockUpdate = (productId, newStock) => {
    const updateProducts = (list) =>
      list.map((p) => (p.id === productId ? { ...p, stock: newStock } : p));

    setProducts(updateProducts);
    setFiltered(updateProducts);
  };

  // Get selected products for print/export
  const getSelectedProducts = () => {
    if (selectedRows.length === 0) return filtered;
    return filtered.filter((p) => selectedRows.includes(p.id));
  };

  // Print function
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Inventory_Report",
  });

  // CSV export function
  const exportToCSV = () => {
    const productsToExport = selectedRows.length > 0 ? getSelectedProducts() : filtered;

    if (productsToExport.length === 0) return;

    // Always use custom CSV export to exclude actions and activity columns
    const csvContent = generateCSV(productsToExport);
    const dateTimeStr = moment().format("YYYY-MM-DDTHHmm");
    downloadCSV(csvContent, `Inventory_Report_${dateTimeStr}`);
  };

  // Generate CSV content from selected products
  const generateCSV = (productsToExport) => {
    const headers = ["Product", "Stock"];
    const rows = productsToExport.map((p) => [p.title || "", p.stock ?? 0]);
    const csvRows = [headers, ...rows].map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    );
    return csvRows.join("\n");
  };

  // Download CSV file
  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // DataGrid columns
  const columns = [
    {
      field: "title",
      headerName: "Item Name",
      flex: 1,
      minWidth: 240,
    },
    {
      field: "stock",
      headerName: "Stock",
      width: 100,
      type: "number",
      sortComparator: (v1, v2) => {
        const stock1 = v1 ?? 0;
        const stock2 = v2 ?? 0;
        return stock1 - stock2;
      },
      renderCell: (params) => {
        const stock = params.value;
        const isLow = stock !== undefined && stock !== null && stock < LOW_STOCK_THRESHOLD;
        return (
          <Typography variant="h4" color={stock < 0 ? "error" : "inherit"}>
            {stock ?? "0"}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const hasStock = params.row.stock > 0;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              color="success"
              onClick={() => handleOpenDialog(params.row, "add")}
              title="Add Stock"
              size="small"
            >
              <AddIcon />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => handleOpenDialog(params.row, "remove")}
              title="Remove Stock"
              size="small"
              disabled={!hasStock}
            >
              <RemoveIcon />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "activity",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          component={Link}
          to={`/inventory/${params.row.id}`}
          title="View Activity"
          size="small"
        >
          <TimelineIcon />
        </IconButton>
      ),
    },
  ];

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Container maxWidth="md">
        <Box mt={6}>
          <Box>
            <Typography variant="h1" gutterBottom>
              Inventory
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Log production to add stock or record losses (spoilage, breakage, shrinkage) to adjust
              inventory levels.
            </Typography>
          </Box>

          <Box my={3}>
            <Paper elevation={0}>
              <Box
                py={0.75}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginRight: "5px",
                }}
              >
                <ItemFilter
                  config={[
                    {
                      name: "category",
                      multiple: true,
                      options: Array.from(
                        new Set(
                          products
                            .flatMap((p) => p.category || [p.type?.toLowerCase()])
                            .filter(Boolean)
                        )
                      ),
                    },
                  ]}
                  onChange={(filterUpdate) => {
                    setProductFilter({ ...productFilter, ...filterUpdate });
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {selectedRows.length > 0 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ minWidth: "fit-content" }}
                    >
                      {selectedRows.length} selected
                    </Typography>
                  )}
                  <IconButton
                    onClick={handlePrint}
                    title="Print"
                    disabled={selectedRows.length === 0 && filtered.length === 0}
                  >
                    <PrintOutlinedIcon />
                  </IconButton>
                  <IconButton
                    onClick={exportToCSV}
                    title="Download CSV"
                    disabled={selectedRows.length === 0 && filtered.length === 0}
                  >
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                  <TextField
                    id="query"
                    value={query}
                    label=""
                    name="query"
                    variant="outlined"
                    size="small"
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Filter by Name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment sx={{ paddingRight: "10px" }}>
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment>
                          {query && (
                            <IconButton onClick={(e) => setQuery("")}>
                              <CloseOutlinedIcon />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "25px",
                        "& fieldset": {
                          borderColor: "primary.secondary",
                        },
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Paper>
            {(query || Object.keys(productFilter).length > 0) && (
              <Box mt={1}>
                <Typography variant="body2" color="textSecondary">
                  Showing {filtered.length} of {products.length} items
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ width: "100%" }}>
            <DataGrid
              apiRef={dataGridApiRef}
              rows={filtered.map((product) => ({
                id: product.id,
                ...product,
              }))}
              columns={columns}
              autoHeight
              checkboxSelection
              disableRowSelectionOnClick
              disableColumnMenu
              rowSelectionModel={selectedRows}
              onRowSelectionModelChange={(newSelection) => {
                setSelectedRows(newSelection);
              }}
              sx={{
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #ECECEC",
                  paddingY: "10px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#ECECEC",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#FAFAFA",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#FFF",
                },
                "& .MuiDataGrid-row.low-stock-row": {
                  backgroundColor: "#fff8e1 !important",
                },
                "& .MuiDataGrid-row.out-of-stock-row": {
                  backgroundColor: "#fdf2f2 !important",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
              }}
            />
          </Box>
        </Box>
      </Container>

      {/* Stock Update Dialog */}
      <StockUpdateDialog
        open={dialogOpen}
        product={selectedProduct}
        action={selectedAction}
        onClose={handleCloseDialog}
        onStockUpdate={handleStockUpdate}
      />

      {/* Hidden printable version */}
      <Box sx={{ display: "none" }}>
        <Box ref={printRef} sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h5" gutterBottom>
                Inventory Report
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {new Date().toLocaleDateString()}
              </Typography>
            </Box>
            <img alt="logo" src={bakeStreetLogo} style={{ maxHeight: "24pt" }} />
          </Box>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #333" }}>
                <th style={{ textAlign: "left", padding: "8px" }}>
                  <Typography variant="overline">Product</Typography>
                </th>
                <th style={{ textAlign: "left", padding: "8px", width: "100px" }}>
                  <Typography variant="overline">Stock</Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {getSelectedProducts()
                .sort((a, b) => a.title.localeCompare(b.title))
                .map((product) => (
                  <tr key={product.id} style={{ borderBottom: "1px solid #ddd" }}>
                    <td style={{ padding: "8px" }}>{product.title}</td>
                    <td style={{ padding: "8px" }}>{product.stock ?? 0}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Box>
      </Box>
    </Box>
  );
}
